import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2, Plus, ExternalLink, CheckCircle, Copy, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { api } from '../../context/AuthContext';

const STATUS_STYLE = {
  draft_created: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  submitted:     'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  live:          'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300',
  failed:        'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  skipped:       'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

const errDetail = (err) => {
  const d = err.response?.data?.detail;
  return typeof d === 'string' ? d : err.message || 'Request failed';
};

function DraftCard({ draft, syndicationId, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(draft.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const markSubmitted = async () => {
    try {
      const { data } = await api.patch(`/syndication/history/${syndicationId}/status`, {
        platform: draft.platform,
        status: 'submitted',
      });
      if (data.success) onUpdate(data.data);
    } catch { /* silent */ }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
          <span className="font-medium text-sm text-slate-900 dark:text-white">{draft.platform_name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_STYLE[draft.status] || STATUS_STYLE.skipped}`}>
            {draft.status?.replace('_', ' ')}
          </span>
          {draft.do_follow && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">DoFollow</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {draft.char_count > 0 && <span className="text-xs text-slate-400">{draft.char_count.toLocaleString()} chars</span>}
          {draft.submit_url && (
            <a href={draft.submit_url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 rounded-md bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Publish
            </a>
          )}
        </div>
      </div>

      {expanded && draft.content && (
        <div className="border-t border-slate-200 dark:border-slate-800 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Platform draft</span>
            <div className="flex gap-2">
              <button onClick={copy} className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                {copied ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              {draft.status === 'draft_created' && (
                <button onClick={markSubmitted} className="text-xs text-emerald-600 hover:underline">Mark submitted</button>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 rounded p-2 max-h-60 overflow-y-auto prose prose-xs dark:prose-invert max-w-none prose-headings:text-sm prose-headings:font-semibold prose-p:my-1 prose-li:my-0 prose-pre:text-xs prose-pre:p-2">
            <ReactMarkdown>{draft.content}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SyndicationPage() {
  const [platforms, setPlatforms] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('create');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSyndication, setActiveSyndication] = useState(null);

  // Form
  const [contentUrl, setContentUrl] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['medium', 'linkedin_article', 'devto']);
  const [tags, setTags] = useState('');

  const load = async () => {
    try {
      const [platRes, histRes] = await Promise.all([
        api.get('/syndication/platforms'),
        api.get('/syndication/history'),
      ]);
      setPlatforms(platRes.data.platforms || []);
      setHistory(histRes.data.history || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const togglePlatform = (id) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSyndicate = async (e) => {
    e.preventDefault();
    if (!contentUrl.trim() || selectedPlatforms.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/syndication/syndicate', {
        content_url: contentUrl.trim(),
        title: title.trim(),
        body: body.trim(),
        platforms: selectedPlatforms,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      if (data.success) {
        setActiveSyndication(data.syndication);
        setActiveTab('drafts');
        await load();
      } else {
        setError(data.error || 'Syndication failed');
      }
    } catch (err) {
      setError(errDetail(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (updated) => {
    setActiveSyndication(updated);
    setHistory((prev) => prev.map((h) => (h.id === updated.id ? updated : h)));
  };

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
          <Share2 className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">NAP Syndication</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Repurpose content into platform-native drafts for Medium, LinkedIn, Dev.to, Hashnode, and more — with rel=canonical preserved.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'create', label: 'Syndicate Content' },
          { id: 'drafts', label: 'Generated Drafts', disabled: !activeSyndication },
          { id: 'history', label: `History (${history.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 disabled:opacity-40'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Create */}
      {activeTab === 'create' && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
          <form onSubmit={handleSyndicate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Content URL *</label>
                <input
                  type="url"
                  value={contentUrl}
                  onChange={(e) => setContentUrl(e.target.value)}
                  placeholder="https://yoursite.com/article"
                  required
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Article Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Article headline"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Article Body (paste text for better results)</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Paste the full article text here..."
                rows={5}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-y"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="seo, marketing, content"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Target Platforms *</label>
              <div className="flex flex-wrap gap-2">
                {platforms.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlatform(p.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors ${
                      selectedPlatforms.includes(p.id)
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-violet-300'
                    }`}
                  >
                    {selectedPlatforms.includes(p.id) && <CheckCircle className="w-3 h-3" />}
                    {p.name}
                    {p.supports_canonical && <span className="text-[10px] text-slate-400">canonical</span>}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={loading || selectedPlatforms.length === 0}
              className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              {loading ? 'Generating drafts...' : 'Syndicate Content'}
            </button>
          </form>
        </div>
      )}

      {/* Drafts */}
      {activeTab === 'drafts' && activeSyndication && (
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
            <p className="font-semibold text-slate-900 dark:text-white">{activeSyndication.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{activeSyndication.source_url}</p>
            <div className="mt-2 flex gap-3 text-xs text-slate-500">
              <span>{activeSyndication.total_drafts} drafts created</span>
            </div>
            {activeSyndication.bookmarking?.length > 0 && (
              <div className="mt-3 border-t border-slate-100 dark:border-slate-800 pt-3">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Promote via bookmarking</p>
                <div className="flex flex-wrap gap-2">
                  {activeSyndication.bookmarking.map((b) => (
                    <a key={b.name} href={b.submit_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-violet-300 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {b.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {(activeSyndication.platforms || []).map((draft) => (
            <DraftCard
              key={draft.platform}
              draft={draft}
              syndicationId={activeSyndication.id}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <p className="text-center text-slate-500 dark:text-slate-400 py-12 text-sm">No syndications yet.</p>
          ) : (
            history.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm flex items-center justify-between gap-4 cursor-pointer hover:border-violet-300 dark:hover:border-violet-700 transition-colors"
                onClick={() => { setActiveSyndication(s); setActiveTab('drafts'); }}
              >
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.source_url}</p>
                </div>
                <div className="text-right text-xs text-slate-500 shrink-0">
                  <p>{s.total_drafts} drafts</p>
                  <p>{new Date(s.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </motion.div>
  );
}
